import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { ApiParams } from '../../../common/api/api';
import SelectDialog, {
  SelectDialogSearchField,
  SelectDialogSubmitButton,
  SelectDialogTable
} from '../../../common/dialogs/SelectDialog';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import ProductAutocomplete from '../components/ProductAutocomplete';
import {
  useFindProductDesignSpecs,
  useProductEntityPrefix
} from '../Product.hooks';
import {
  BaseProductAssignDialogProps,
  ProductDesignSpec
} from '../Product.interface';
import { getProductEntityName } from '../Product.config';

export interface AssignDesignSpecDialogProps
  extends Omit<
    BaseProductAssignDialogProps<never, ProductDesignSpec>,
    'onAssigned' | 'productId'
  > {
  productId?: number;
  title?: string;
  contentText?: string;
  isLoading?: boolean;
  defaultLimit?: number;
  onSubmit?: (designSpecs: ProductDesignSpec[]) => void;
}

const AssignDesignSpecDialog: React.FC<AssignDesignSpecDialogProps> = props => {
  const {
    open,
    selectedItems,
    isLoading = false,
    defaultLimit = 5,
    title = `Assign ${getProductEntityName('designSpec')}`,
    contentText = `Search for ${getProductEntityName('designSpec')} to assign.`,
    onCancel,
    onError,
    onSubmit
  } = props;
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(defaultLimit);
  const [productId, setProductId] = useState(props.productId);
  const {
    productDesignSpecList,
    isLoadingProductDesignSpecs,
    loadingProductDesignSpecsError,
    findProductDesignSpecs
  } = useFindProductDesignSpecs(productId || 0);
  const prefix: string = useProductEntityPrefix(
    ProductBoardColumnEntity.DesignSpec
  );

  // handle searching
  useEffect(() => {
    const params: ApiParams = { search, page, limit };
    findProductDesignSpecs(params);
  }, [search, limit, page, productId, findProductDesignSpecs]);

  useEffect(() => {
    if (loadingProductDesignSpecsError) {
      onError?.(loadingProductDesignSpecsError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingProductDesignSpecsError]);

  return (
    <SelectDialog open={open} onClose={onCancel} selectedItems={selectedItems}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{contentText}</DialogContentText>
        <SelectDialogSearchField
          label={getProductEntityName('designSpec')}
          onSearch={(value: string) => {
            setPage(0);
            setSearch(value);
          }}
        />
        <Box mt={1} mb={2} className='search-box'>
          <ProductAutocomplete
            productId={productId}
            onChange={product => {
              if (product) {
                setProductId(product.id);
              }
            }}
          />
        </Box>
        <SelectDialogTable
          multiple
          idKey='id'
          columns={[
            {
              key: 'id',
              label: 'ID',
              render: (item: ProductDesignSpec) => prefix + item.id
            },
            { key: 'title', label: 'Title', wrap: true },
            {
              key: 'task.status',
              label: 'Status',
              render: (item: ProductDesignSpec) => {
                return item.task && <TaskStatusLabel task={item.task} />;
              }
            }
          ]}
          isLoading={isLoadingProductDesignSpecs || isLoading}
          dataList={productDesignSpecList}
          page={page}
          rowsPerPage={limit}
          onChangePage={page => setPage(page)}
          onChangeRowsPerPage={limit => setLimit(limit)}
        />
        {loadingProductDesignSpecsError && (
          <Alert severity='error'>
            Error getting data. Please check your internet connection and try
            again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <SelectDialogSubmitButton onSubmit={onSubmit}>
          Assign
        </SelectDialogSubmitButton>
      </DialogActions>
    </SelectDialog>
  );
};

export default AssignDesignSpecDialog;
